import React, { useMemo, useState } from "react";

/**
 * Questionnaire d'onboarding Tengoku Dōjō
 * Design : fond blanc, texte noir, accents rouges.
 * Une question à la fois + cases à cocher + bouton "Suivant".
 * À la fin : génère un résumé copiable à renvoyer à l'agent.
 */

const RED = "var(--brand)";

const STEPS = [
  {
    id: "auth",
    kanji: "鍵",
    title: "Méthodes de connexion",
    subtitle: "Comment les membres se connecteront-ils à la plateforme ?",
    multi: false,
    options: [
      { value: "email", label: "Email + mot de passe uniquement" },
      { value: "google", label: "Google uniquement (Emergent-managed)" },
      { value: "both", label: "Les deux : Email/mot de passe + Google", recommended: true },
    ],
  },
  {
    id: "admin",
    kanji: "師",
    title: "Ton compte administrateur (créateur)",
    subtitle: "Comment veux-tu obtenir ton accès admin ?",
    multi: false,
    options: [
      { value: "default", label: "Crée-moi un compte admin par défaut", recommended: true, needsInput: true, inputLabel: "Email + mot de passe souhaités (ex: admin@tengoku.com / MonMotDePasse)" },
      { value: "self", label: "Je m'inscris moi-même, tu me passes en admin ensuite" },
    ],
  },
  {
    id: "discord",
    kanji: "縁",
    title: "Communauté Discord",
    subtitle: "Lien d'invitation + synchronisation du rôle « Abonné ».",
    multi: false,
    options: [
      { value: "link_later_sync", label: "Lien maintenant + sync du rôle plus tard (P2)", recommended: true, needsInput: true, inputLabel: "Colle ton lien d'invitation Discord" },
      { value: "link_now_sync", label: "Lien maintenant + sync du rôle dès le MVP (bot Discord requis)", needsInput: true, inputLabel: "Colle ton lien d'invitation Discord" },
      { value: "later", label: "Je te donnerai le lien Discord plus tard" },
    ],
  },
  {
    id: "coaching",
    kanji: "導",
    title: "Packs de coaching 1:1 (3 / 6 / 12 mois)",
    subtitle: "Comment gérer les offres d'accompagnement individuel ?",
    multi: false,
    options: [
      { value: "form", label: "Simple bouton « Réserver un coaching » = formulaire de demande, sans paiement", recommended: true },
      { value: "stripe", label: "Paiement Stripe des packs dès maintenant", needsInput: true, inputLabel: "Prix des 3 durées (ex: 3 mois=299€, 6 mois=549€, 12 mois=999€)" },
    ],
  },
  {
    id: "media",
    kanji: "蔵",
    title: "Stockage des médias (images / audio)",
    subtitle: "Où stocker les fichiers pour le MVP ?",
    multi: false,
    options: [
      { value: "base64", label: "Base64 dans MongoDB (simple, rapide)", recommended: true },
      { value: "object", label: "Object storage dès maintenant (plus complexe, gros volumes)" },
    ],
  },
  {
    id: "theme",
    kanji: "彩",
    title: "Thème Tengoku Dōjō (fond blanc / noir / rouge)",
    subtitle: "Comment appliquer ta nouvelle identité visuelle ?",
    multi: false,
    options: [
      { value: "default_keep", label: "Thème par défaut + garder les autres thèmes en option", recommended: true },
      { value: "only", label: "Seul thème disponible (retirer les thèmes sombres)" },
      { value: "default_dark", label: "Par défaut + créer aussi une variante sombre (inversée) rouge" },
    ],
  },
];

export default function Questionnaire() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [inputs, setInputs] = useState({});
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  const current = STEPS[step];
  const total = STEPS.length;
  const progress = Math.round(((done ? total : step) / total) * 100);

  const selected = answers[current?.id];
  const selectedOpt = current?.options.find((o) => o.value === selected);
  const canNext = !!selected && (!selectedOpt?.needsInput || (inputs[current.id] || "").trim().length > 0);

  const summary = useMemo(() => {
    const lines = STEPS.map((s) => {
      const val = answers[s.id];
      const opt = s.options.find((o) => o.value === val);
      let line = `• ${s.title} → ${opt ? opt.label : "(non répondu)"}`;
      if (opt?.needsInput && inputs[s.id]) line += ` [${inputs[s.id].trim()}]`;
      return line;
    });
    return "Réponses questionnaire Tengoku Dōjō :\n" + lines.join("\n");
  }, [answers, inputs]);

  const pick = (val) => {
    setAnswers((a) => ({ ...a, [current.id]: val }));
  };

  const next = () => {
    if (step + 1 < total) setStep((s) => s + 1);
    else setDone(true);
  };
  const back = () => {
    if (done) { setDone(false); return; }
    if (step > 0) setStep((s) => s - 1);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* ignore */ }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* En-tête */}
        <div style={styles.header}>
          <div style={styles.brandRow}>
            <span style={styles.kanjiBrand}>天</span>
            <span style={styles.brand}>TENGOKU <span style={{ color: RED }}>DŌJŌ</span></span>
          </div>
          <div style={styles.progressWrap}>
            <div style={styles.progressBar}>
              <div style={{ ...styles.progressFill, width: `${progress}%` }} />
            </div>
            <span style={styles.progressText}>{done ? "Terminé" : `Étape ${step + 1} / ${total}`}</span>
          </div>
        </div>

        {!done ? (
          <div style={styles.body}>
            <div style={styles.kanjiWatermark}>{current.kanji}</div>
            <h1 style={styles.title}>{current.title}</h1>
            <p style={styles.subtitle}>{current.subtitle}</p>

            <div style={styles.options}>
              {current.options.map((o) => {
                const isSel = selected === o.value;
                return (
                  <button
                    key={o.value}
                    onClick={() => pick(o.value)}
                    style={{
                      ...styles.option,
                      borderColor: isSel ? RED : "var(--border-c)",
                      boxShadow: isSel ? `0 0 0 2px color-mix(in srgb, var(--brand) 13%, transparent)` : "none",
                      background: isSel ? "color-mix(in srgb, var(--brand) 8%, transparent)" : "#fff",
                    }}
                  >
                    <span style={{ ...styles.checkbox, borderColor: isSel ? RED : "#c9c9c9", background: isSel ? RED : "#fff" }}>
                      {isSel && <span style={styles.checkmark}>✓</span>}
                    </span>
                    <span style={styles.optLabel}>
                      {o.label}
                      {o.recommended && <span style={styles.reco}>recommandé</span>}
                    </span>
                  </button>
                );
              })}
            </div>

            {selectedOpt?.needsInput && (
              <div style={styles.inputWrap}>
                <label style={styles.inputLabel}>{selectedOpt.inputLabel}</label>
                <input
                  style={styles.input}
                  value={inputs[current.id] || ""}
                  onChange={(e) => setInputs((i) => ({ ...i, [current.id]: e.target.value }))}
                  placeholder="Écris ici…"
                />
              </div>
            )}

            <div style={styles.actions}>
              <button onClick={back} disabled={step === 0} style={{ ...styles.btnGhost, opacity: step === 0 ? 0.4 : 1 }}>
                Précédent
              </button>
              <button onClick={next} disabled={!canNext} style={{ ...styles.btnPrimary, opacity: canNext ? 1 : 0.4 }}>
                {step + 1 === total ? "Terminer" : "Suivant →"}
              </button>
            </div>
          </div>
        ) : (
          <div style={styles.body}>
            <div style={styles.kanjiWatermark}>完</div>
            <h1 style={styles.title}>Merci ! Voici ton récapitulatif</h1>
            <p style={styles.subtitle}>Copie ce texte et renvoie-le moi dans le chat pour lancer la Phase 1.</p>
            <pre style={styles.summary}>{summary}</pre>
            <div style={styles.actions}>
              <button onClick={back} style={styles.btnGhost}>Modifier</button>
              <button onClick={copy} style={styles.btnPrimary}>{copied ? "Copié ✓" : "Copier le récapitulatif"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    width: "100%",
    background: "var(--bg-secondary)",
    color: "var(--text-primary)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: 720,
    background: "var(--bg-secondary)",
    border: "1px solid #ededed",
    borderRadius: 20,
    boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  header: { padding: "24px 32px", borderBottom: "1px solid #f0f0f0" },
  brandRow: { display: "flex", alignItems: "baseline", gap: 12, marginBottom: 16 },
  kanjiBrand: { fontSize: 20, color: "var(--text-primary)", letterSpacing: 2 },
  brand: { fontSize: 20, fontWeight: 800, letterSpacing: 3, color: "var(--text-primary)" },
  progressWrap: { display: "flex", alignItems: "center", gap: 12 },
  progressBar: { flex: 1, height: 6, background: "var(--border-c)", borderRadius: 999, overflow: "hidden" },
  progressFill: { height: "100%", background: RED, transition: "width .3s ease" },
  progressText: { fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", whiteSpace: "nowrap" },
  body: { padding: "40px 32px", position: "relative" },
  kanjiWatermark: {
    position: "absolute", top: 8, right: 24, fontSize: 120, lineHeight: 1,
    color: "var(--bg-tertiary)", fontWeight: 700, userSelect: "none", pointerEvents: "none",
  },
  title: { fontSize: 26, fontWeight: 800, margin: "0 0 8px", color: "var(--text-primary)", position: "relative" },
  subtitle: { fontSize: 15, color: "var(--text-secondary)", margin: "0 0 28px", position: "relative" },
  options: { display: "flex", flexDirection: "column", gap: 12, position: "relative" },
  option: {
    display: "flex", alignItems: "center", gap: 14, textAlign: "left",
    padding: "16px 18px", border: "1.5px solid #e5e5e5", borderRadius: 12,
    cursor: "pointer", transition: "all .15s ease", width: "100%",
  },
  checkbox: {
    width: 22, height: 22, borderRadius: 6, border: "2px solid #c9c9c9",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    transition: "all .15s ease",
  },
  checkmark: { color: "#fff", fontSize: 14, fontWeight: 900, lineHeight: 1 },
  optLabel: { fontSize: 15, color: "var(--text-primary)", fontWeight: 500, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  reco: { fontSize: 11, fontWeight: 700, color: RED, background: "color-mix(in srgb, var(--brand) 10%, transparent)", border: `1px solid color-mix(in srgb, var(--brand) 20%, transparent)`, padding: "2px 8px", borderRadius: 999 },
  inputWrap: { marginTop: 20, position: "relative" },
  inputLabel: { display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 8 },
  input: {
    width: "100%", padding: "12px 14px", border: "1.5px solid #e0e0e0",
    borderRadius: 10, fontSize: 14, outline: "none", color: "var(--text-primary)",
  },
  actions: { display: "flex", justifyContent: "space-between", gap: 12, marginTop: 32, position: "relative" },
  btnGhost: {
    padding: "12px 22px", borderRadius: 10, border: "1.5px solid #ddd",
    background: "var(--bg-secondary)", color: "var(--text-secondary)", fontWeight: 600, cursor: "pointer", fontSize: 14,
  },
  btnPrimary: {
    padding: "12px 28px", borderRadius: 10, border: "none",
    background: RED, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14,
    boxShadow: `0 6px 18px color-mix(in srgb, var(--brand) 27%, transparent)`,
  },
  summary: {
    background: "var(--bg-tertiary)", border: "1px solid #eee", borderRadius: 12,
    padding: "18px", fontSize: 13.5, color: "var(--text-primary)", whiteSpace: "pre-wrap",
    lineHeight: 1.7, position: "relative", fontFamily: "inherit",
  },
};
